/**
 * Trim a numeric string to at most `maxDecimals` digits after the decimal
 * point, leaving the integer part and any in-progress trailing dot intact.
 * Used by money/price inputs to cap precision (e.g. 2 decimals) without
 * blocking the keystroke outright.
 */
export function clampDecimals(value: string, maxDecimals: number): string {
  const dot = value.indexOf('.');
  if (dot === -1 || value.length - dot - 1 <= maxDecimals) return value;
  return maxDecimals === 0 ? value.slice(0, dot) : value.slice(0, dot + 1 + maxDecimals);
}

/**
 * Normalise free-typed money input to a canonical decimal string and clamp it
 * to `maxDecimals`. Accepts a comma as a decimal separator (common on European
 * keyboards) and rewrites it to a dot, strips any other non-numeric characters,
 * and keeps a single decimal point. Always emits `.` as the separator so the
 * value is locale-independent — unlike `<input type="number">`, which renders
 * the separator per the browser locale and can flip a `.` to `,`.
 */
export function sanitizeDecimalInput(raw: string, maxDecimals: number): string {
  // Treat the first comma or dot as the decimal separator; drop the rest.
  let s = raw.replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  return clampDecimals(s, maxDecimals);
}
